import { useCallback, useContext, useState } from 'react'
import { useStore } from 'react-redux'
import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios'
import JWT from 'jsonwebtoken'
import 'whatwg-fetch'
import { useTypedSelector } from '../State/RootReducer'
import { Decoded } from '../Types/globalTypes'
import { useLocation, useNavigate } from 'react-router-dom'
import { stringifyError, useNewRelic } from '../Libs/observability-tool'
import { AuthContext, useUserId } from '../Libs/authentication'
import { clearCacheExceptAppVersion } from '../Libs/cache'

interface AuthFetchTokens {
  reAuthToken: string
  token: string
}

export const fetchAuthTokens = async (reAuthToken: string, cancelToken: CancelToken): Promise<AxiosResponse<AuthFetchTokens>> => {
  const data = new FormData()
  data.append('reAuthToken', reAuthToken)

  const url = (process.env.REACT_APP_AUTH_API_ENDPOINT ?? '') + (process.env.REACT_APP_API_ENDPOINT_AUTHROUTE ?? '')
  const response = await axios.post(url + '/re-auth', data, {
    cancelToken,
  })

  if (response.status < 200 || response.status >= 300) {
    return Promise.reject(new Error('re-auth failed HTTP code ' + response.status))
  }

  return response
}

/**
 * Custom hook that handles the re-login process for the application.
 */
export const useRelogin = (): (() => Promise<string>) => {
  const { login, logout, reAuthToken } = useContext(AuthContext)
  const store = useStore()
  const source = axios.CancelToken.source()
  const [reloginPromise, setReloginPromise] = useState<Promise<string> | undefined>(undefined)
  /** When user logs out, we set this value to clear the cache on login page */
  const logoutIsActive = useTypedSelector(state => state.app.isLogoutActive)
  const navigate = useNavigate()
  const { pageActions } = useNewRelic()
  const { updateUserId } = useUserId()
  const { pathname } = useLocation()

  const clearCache = (): void => {
    store._PERSISTOR.purge()

    /* sometimes the store purge does not work we clean also the used localforage */
    clearCacheExceptAppVersion()

    /** reset the auth token because it is saved in the app memory and not in web storage */
    logout()
  }

  const navigateToLogin = (): void => {
    navigate('/login')
  }

  const _fetch = useCallback(async (): Promise<string> => {
    if (reloginPromise) return reloginPromise

    // eslint-disable-next-line no-async-promise-executor
    const _reloginPromise: Promise<string> = new Promise(async (resolve, reject) => {
      if (logoutIsActive) return

      /* check reAuthToken is not expired */
      if (reAuthToken) {
        const decoded = JWT.decode(reAuthToken) as Decoded

        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          clearCache()
          navigateToLogin()
          return reject(new Error('reAuthToken expired'))
        }
      }

      if (!reAuthToken) {
        if (pathname !== '/login') {
          clearCache()
        }
        return reject(new Error('reAuthToken undefined'))
      }

      try {
        const { data } = await fetchAuthTokens(reAuthToken, source.token)
        const newAuthToken = data.token
        const newReAuthToken = data.reAuthToken
        const newDecoded = JWT.decode(newAuthToken) as Decoded

        login(newAuthToken, newReAuthToken)
        updateUserId(newDecoded.userId)

        return resolve(newAuthToken)
      } catch (err) {
        pageActions.logEvent({ logValue: `relogin failed: ${stringifyError(err as Error)}` })

        /* on 400 error, logout the user and redirect to login page. */
        if ((err as AxiosError)?.response?.status === 400) {
          clearCache()
          navigateToLogin()
          return
        }

        reject(err as Error)
      } finally {
        setReloginPromise(undefined)
      }
    })

    setReloginPromise(_reloginPromise)

    return _reloginPromise
  }, [reAuthToken, logout]) //eslint-disable-line

  return _fetch
}
